import MessageLinks from "./MessageLinks";
import { track } from "../lib/contact";
import React, { useState } from "react";

const ContactForm: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send message");
      }

      track("contact_click", { channel: "form", where: "contact" });
      setSuccess("Message sent! We'll be in touch soon.");
      setName("");
      setEmail("");
      setMessage("");
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong. Try emailing us directly at hello@labradical.com.au");
    } finally {
      setLoading(false);
    }
  };

  if (submitted && success) {
    return (
      <section className="px-6 py-20 laptop:px-10">
        <div className="mx-auto max-w-xl text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-lime/10">
            <svg className="h-8 w-8 text-lime" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="font-heading mb-3 text-2xl font-bold">Thanks for reaching out!</h2>
          <p className="text-muted">{success}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="reveal border-b border-border px-6 py-20 opacity-0 transition-all duration-[0.65s] laptop:px-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-4 flex items-center gap-2.5 text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
          <span className="inline-block h-px w-5 shrink-0 bg-lime" />
          Get in touch
        </div>
        <h2 className="font-heading mb-8 text-[clamp(28px,4vw,36px)] font-bold -tracking-[0.03em] leading-[1.05]">
          Tell us about your project
        </h2>
        <div className="-mt-4 mb-8">
          <MessageLinks where="contact" />
        </div>

        <form onSubmit={handleSubmit} className="grid gap-5">
          {(error || success) && (
            <div className={`rounded-md px-5 py-3 ${success ? "bg-lime/10 text-lime" : "bg-coral/10 text-coral"}`}>
              <p className="text-sm">{error || success}</p>
            </div>
          )}

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            className="w-full rounded-md border border-border bg-surface p-3 text-sm text-text placeholder-muted outline-none transition-[border-color] focus:border-lime/40"
          />

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            required
            className="w-full rounded-md border border-border bg-surface p-3 text-sm text-text placeholder-muted outline-none transition-[border-color] focus:border-lime/40"
          />

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell us about your project..."
            rows={5}
            required
            className="w-full rounded-md border border-border bg-surface p-3 text-sm text-text placeholder-muted outline-none transition-[border-color] focus:border-lime/40"
          />

          <button
            type="submit"
            disabled={!name || !email || !message || loading}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lime px-8 py-[0.9rem] text-[13px] font-bold text-bg transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? "Sending..." : "Send message →"}
          </button>

          <p className="text-center text-xs text-muted">
            Or email us directly at{" "}
            <a href="mailto:hello@labradical.com.au" className="text-lime no-underline hover:underline">
              hello@labradical.com.au
            </a>
          </p>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
